// Rich editable for FEEL text. One contenteditable span:
//   • Focused  — plain text (user types freely).
//   • Blurred  — the text is lexed and identifiers that exist in the
//                provided scope are rendered as colored chips. Non-matching
//                identifiers render as red dashed chips.
//
// `textContent` of the span always returns the raw FEEL text — both when the
// span contains chips (concatenated names + surrounding text) and when the
// user is typing. That lets callers keep using `span.textContent` the way
// they do with the plain `makeEditable()`.
//
// `getScope` is a callback so the scope can update between renders.

import { tokenize, T, BUILTIN_ALIASES } from '../feel/index';

const SYNTAX_CLASS_BY_TOKEN = {
  [T.NUMBER]:  'number',
  [T.STRING]:  'string',
  [T.BOOLEAN]: 'literal',
  [T.NULL]:    'literal',
  [T.KEYWORD]: 'keyword',
  [T.OP]:      'op',
  [T.PUNCT]:   'punct',
  [T.DASH]:    'punct',
};

export function tokenizeForDisplay(text, scopeByName) {
  const src = text == null ? '' : String(text);
  if (!src) return [];
  let tokens;
  try { tokens = tokenize(src); }
  catch { return [{ type: 'text', value: src }]; }

  // Track the previous "structural" punct so we can detect key positions.
  // An IDENT followed by ':' is a *label* (context-entry key or named-arg
  // name) when it appears right after '{', '(', or ',' — never a referenced
  // variable, so don't try to look it up in scope.
  const isKeyOpener = (tk) => tk && tk.type === T.PUNCT && (tk.value === '{' || tk.value === '(' || tk.value === ',');

  const out = [];
  let cursor = 0;
  let prevStructural = { type: T.PUNCT, value: '{' }; // start-of-input behaves like an opener
  for (let i = 0; i < tokens.length; i++) {
    const tk = tokens[i];
    if (tk.type === T.EOF) break;
    if (tk.start > cursor) out.push({ type: 'text', value: src.slice(cursor, tk.start) });
    if (tk.type === T.IDENT) {
      const next = tokens[i + 1];
      const isLabel = next
        && next.type === T.PUNCT
        && next.value === ':'
        && isKeyOpener(prevStructural);
      if (isLabel) {
        out.push({ type: 'syntax', kind: 'label', value: tk.value });
      } else {
        const entry = scopeByName && scopeByName.get ? scopeByName.get(tk.value) : null;
        // Scope wins over built-ins so a user-declared variable can shadow a
        // built-in name (FEEL semantics, and matches what the evaluator does).
        if (entry) {
          out.push({
            type: 'ident',
            name: tk.value,
            known: true,
            kind: entry.kind,
            typeRef: entry.typeRef,
            sourceName: entry.sourceName,
          });
        } else if (Object.prototype.hasOwnProperty.call(BUILTIN_ALIASES, tk.value)) {
          out.push({ type: 'builtin', name: tk.value });
        } else {
          out.push({ type: 'ident', name: tk.value, known: false, kind: null, typeRef: null, sourceName: null });
        }
      }
    } else {
      const cls = SYNTAX_CLASS_BY_TOKEN[tk.type];
      const raw = src.slice(tk.start, tk.end);
      if (cls) out.push({ type: 'syntax', kind: cls, value: raw });
      else out.push({ type: 'text', value: raw });
    }
    cursor = tk.end;
    prevStructural = tk;
  }
  if (cursor < src.length) out.push({ type: 'text', value: src.slice(cursor) });
  return out;
}

function displayTypeShort(typeRef) {
  if (!typeRef) return 'any';
  return String(typeRef).replace(/^[^:]+:/, '');
}

function indexScope(scope) {
  const map = new Map();
  (scope || []).forEach((v) => {
    if (!v || !v.name) return;
    if (!map.has(v.name)) map.set(v.name, v);
  });
  return map;
}

function renderChippedInto(el, text, scopeMap) {
  el.innerHTML = '';
  if (!text) return;
  const segments = tokenizeForDisplay(text, scopeMap);
  segments.forEach((seg) => {
    if (seg.type === 'text') {
      el.appendChild(document.createTextNode(seg.value));
    } else if (seg.type === 'syntax') {
      const span = document.createElement('span');
      span.className = `epx-dmn-feel-tok epx-dmn-feel-tok--${seg.kind}`;
      span.textContent = seg.value;
      el.appendChild(span);
    } else if (seg.type === 'builtin') {
      const span = document.createElement('span');
      span.className = 'epx-dmn-feel-builtin';
      span.textContent = seg.name;
      span.title = `${seg.name} — función FEEL`;
      el.appendChild(span);
    } else {
      const chip = document.createElement('span');
      const kindClass = seg.known ? (seg.kind || 'known') : 'unknown';
      chip.className = `epx-dmn-feel-chip epx-dmn-feel-chip--${kindClass}`;
      chip.textContent = seg.name;
      chip.setAttribute('contenteditable', 'false');
      if (seg.known) {
        const t = displayTypeShort(seg.typeRef);
        chip.title = `${seg.name} : ${t}${seg.sourceName ? ' — ' + seg.sourceName : ''}`;
      } else {
        chip.title = `${seg.name} — no está en scope`;
      }
      el.appendChild(chip);
    }
  });
}

export function makeFeelEditable(initialValue, placeholder, commit, getScope, extraClass) {
  const span = document.createElement('span');
  span.className = 'epx-dmn-expr__editable epx-dmn-feel-field' + (extraClass ? ' ' + extraClass : '');
  span.setAttribute('contenteditable', 'true');
  span.setAttribute('spellcheck', 'false');

  let current = initialValue == null ? '' : String(initialValue);

  const renderChipped = () => {
    const scopeMap = indexScope(typeof getScope === 'function' ? getScope() : []);
    renderChippedInto(span, current, scopeMap);
    if (!current) {
      span.classList.add('epx-dmn-expr__editable--empty');
      span.dataset.placeholder = placeholder || '';
    } else {
      span.classList.remove('epx-dmn-expr__editable--empty');
    }
  };

  const flattenToPlainText = () => {
    span.textContent = current;
    span.classList.remove('epx-dmn-expr__editable--empty');
  };

  span.addEventListener('focus', () => {
    // Switch to plain text while editing. Preserve caret at end.
    flattenToPlainText();
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(span);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  });

  span.addEventListener('blur', () => {
    const next = (span.textContent || '').trim();
    current = next;
    renderChipped();
    commit(current);
  });

  span.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); span.blur(); }
    if (e.key === 'Escape') { e.preventDefault(); span.textContent = current; span.blur(); }
  });

  renderChipped();
  return span;
}
